import React from "react";
import sanitizeHtml from "sanitize-html";
import {
  Iconography,
  IconographySizes,
  Loader,
  LoaderSizes,
  Modal,
} from "@hid-galaxy-ui/galaxy-react";
import { useAppDispatch, useAppSelector } from "../../../../../../redux";
import { getPhasesSagaAction } from "../../../../../../redux/saga/technology-settings/phases/saga-actions";
import type { IntegrationPhase } from "./IntegrationTimelineSection";
import "./PhaseDescriptionModal.scss";

type PhaseDescriptionModalProps = {
  selectedPhase: IntegrationPhase;
  onClose: () => void;
};

const sanitizeDescriptionHtml = (content?: string): string => {
  if (!content?.trim()) {
    return "No description available.";
  }

  const hasHtmlTags = /<\/?[a-z][\s\S]*>/i.test(content);
  if (!hasHtmlTags) {
    return sanitizeHtml(content, {
      allowedTags: [],
      allowedAttributes: {},
    }).replace(/\r\n|\r|\n/g, "<br />");
  }

  return sanitizeHtml(content, {
    allowedTags: [
      "p",
      "br",
      "ul",
      "ol",
      "li",
      "strong",
      "b",
      "em",
      "i",
      "u",
      "h1",
      "h2",
      "h3",
      "h4",
      "span",
      "div",
    ],
    allowedAttributes: {
      span: ["style"],
      p: ["style"],
      div: ["style"],
    },
  });
};

const PhaseDescriptionModal: React.FC<PhaseDescriptionModalProps> = ({
  selectedPhase,
  onClose,
}) => {
  const dispatch = useAppDispatch();
  const { phases, status } = useAppSelector(
    (state) => state.technology_settings_phases
  );

  React.useEffect(() => {
    dispatch(getPhasesSagaAction());
  }, [dispatch]);

  const selectedPhaseData = React.useMemo(() => {
    return phases.find(
      (phase) => phase.name.toLowerCase() === selectedPhase.toLowerCase()
    );
  }, [phases, selectedPhase]);

  const isLoading = status.phasesLoading;

  return (
    <Modal
      headerText=""
      width={720}
      onClose={onClose}
      className="phase-description-modal"
    >
      <div className="phase-description-modal__content">
        <div className="phase-description-modal__header">
          <div className="phase-description-modal__header-copy">
            <span className="phase-description-modal__eyebrow">
              Integration details
            </span>
            <h2 className="phase-description-modal__title">
              Phase and Activity Description
            </h2>
            <p className="phase-description-modal__subtitle">
              Integration Phase - {selectedPhase}
            </p>
          </div>
          <button
            type="button"
            className="phase-description-modal__close"
            onClick={onClose}
            aria-label="Close"
          >
            <Iconography icon="xmark" size={IconographySizes.Medium} />
          </button>
        </div>

        <div className="phase-description-modal__body">
          {isLoading ? (
            <div className="phase-description-modal__loading">
              <Loader variant={LoaderSizes.large} />
            </div>
          ) : !selectedPhaseData ? (
            <p className="phase-description-modal__empty">
              No phase description available.
            </p>
          ) : (
            <div className="phase-description-modal__sections">
              <section className="phase-description-modal__section">
                <div className="phase-description-modal__section-header">
                  <h3 className="phase-description-modal__section-title">
                    {selectedPhaseData.name}
                  </h3>
                  <span
                    className="phase-description-modal__section-divider"
                    aria-hidden="true"
                  />
                </div>
                <div
                  className="phase-description-modal__section-box"
                  dangerouslySetInnerHTML={{
                    __html: sanitizeDescriptionHtml(
                      selectedPhaseData.description
                    ),
                  }}
                />
              </section>

              {(selectedPhaseData.activities || []).map((activity, index) => (
                <section
                  key={activity.id}
                  className="phase-description-modal__section"
                >
                  <div className="phase-description-modal__section-header">
                    <h3 className="phase-description-modal__section-title">
                      {index + 1}. {activity.name}
                    </h3>
                    <span
                      className="phase-description-modal__section-divider"
                      aria-hidden="true"
                    />
                  </div>
                  <div
                    className="phase-description-modal__section-box"
                    dangerouslySetInnerHTML={{
                      __html: sanitizeDescriptionHtml(activity.description),
                    }}
                  />
                </section>
              ))}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default PhaseDescriptionModal;
